import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmProvider";
import api, { formatApiError } from "@/lib/api";
import EmployeePicker from "@/components/EmployeePicker";
import { toast } from "sonner";
import { TaskAttachments } from "@/components/TaskAttachments";
import { Plus, Pencil, Trash2, CheckCircle2, Clock, AlertTriangle, ListTodo, Filter } from "lucide-react";

const STATUS_PILL = {
    pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
    in_progress: "bg-blue-50 text-blue-700 border-blue-200",
    done: "bg-green-50 text-green-700 border-green-200",
    cancelled: "bg-ink/5 text-ink/60 border-ink/10",
};

const PRIORITY_PILL = {
    low: "bg-ink/5 text-ink/60 border-ink/10",
    normal: "bg-sky-50 text-sky-700 border-sky-200",
    high: "bg-red-50 text-red-700 border-red-200",
};

function todayISO() {
    return new Date().toISOString().slice(0, 10);
}

const blankForm = () => ({
    id: null,
    employee_id: "",
    title: "",
    description: "",
    due_date: todayISO(),
    priority: "normal",
    status: "pending",
    score: "",
    completion_note: "",
});

export default function DailyTasks() {
    const { user } = useAuth();
    const role = user?.role;
    const isManager = ["admin", "hr", "department_lead", "appraiser"].includes(role);
    const { confirm } = useConfirm();

    const [tasks, setTasks] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({ status: "all", employee_id: "", from_date: "", to_date: "" });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(blankForm());
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter.status && filter.status !== "all") params.status = filter.status;
            if (filter.employee_id) params.employee_id = filter.employee_id;
            if (filter.from_date) params.from_date = filter.from_date;
            if (filter.to_date) params.to_date = filter.to_date;
            const { data } = await api.get("/tasks", { params });
            setTasks(data || []);
        } catch (e) {
            toast.error(formatApiError(e, "Failed to load tasks"));
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!isManager) return;
        api.get("/employees")
            .then(({ data }) => setEmployees(data || []))
            .catch(() => setEmployees([]));
    }, [isManager]);

    const openCreate = () => { setForm(blankForm()); setDialogOpen(true); };
    const openEdit = (t) => {
        setForm({
            id: t.id,
            employee_id: t.employee_id,
            title: t.title || "",
            description: t.description || "",
            due_date: (t.due_date || "").slice(0, 10),
            priority: t.priority || "normal",
            status: t.status || "pending",
            score: t.score == null ? "" : t.score,
            completion_note: t.completion_note || "",
            attachments: t.attachments || [],
        });
        setDialogOpen(true);
    };

    const closeDialog = () => { if (!saving) { setDialogOpen(false); if (form.id) load(); } };

    const save = async () => {
        if (!form.title.trim()) { toast.error("Title is required"); return; }
        if (!form.id && !form.employee_id) { toast.error("Pick an employee"); return; }
        if (!form.due_date) { toast.error("Due date is required"); return; }
        setSaving(true);
        try {
            if (form.id) {
                const payload = {
                    title: form.title,
                    description: form.description,
                    due_date: form.due_date,
                    priority: form.priority,
                    status: form.status,
                    completion_note: form.completion_note,
                };
                if (form.score !== "" && form.score !== null) payload.score = Number(form.score);
                await api.put(`/tasks/${form.id}`, payload);
                toast.success("Task updated");
            } else {
                await api.post("/tasks", {
                    employee_id: form.employee_id,
                    title: form.title,
                    description: form.description,
                    due_date: form.due_date,
                    priority: form.priority,
                });
                toast.success("Task assigned");
            }
            setDialogOpen(false);
            await load();
        } catch (e) {
            toast.error(formatApiError(e, "Save failed"));
        } finally {
            setSaving(false);
        }
    };

    const markStatus = async (t, status) => {
        try {
            await api.put(`/tasks/${t.id}`, { status });
            await load();
        } catch (e) {
            toast.error(formatApiError(e, "Update failed"));
        }
    };

    const remove = async (t) => {
        const ok = await confirm({
            title: "Delete task",
            description: `Are you sure you want to delete "${t.title}"? This cannot be undone.`,
            confirmLabel: "Delete",
            danger: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/tasks/${t.id}`);
            toast.success("Task deleted");
            await load();
        } catch (e) {
            toast.error(formatApiError(e, "Delete failed"));
        }
    };

    const counts = useMemo(() => {
        const c = { total: tasks.length, pending: 0, in_progress: 0, done: 0, late: 0 };
        const today = todayISO();
        tasks.forEach((t) => {
            if (t.status === "pending") c.pending += 1;
            else if (t.status === "in_progress") c.in_progress += 1;
            else if (t.status === "done") c.done += 1;
            if (t.status !== "done" && t.status !== "cancelled" && t.due_date && t.due_date < today) {
                c.late += 1;
            }
        });
        return c;
    }, [tasks]);

    return (
        <div className="px-5 sm:px-8 md:px-12 py-8 sm:py-12 md:py-16" data-testid="daily-tasks-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Daily operations</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02]">
                        Tasks
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-xl">
                        {isManager
                            ? (role === "department_lead"
                                ? "Assign and review daily jobs across your department. Track completion, on-time delivery, and individual performance."
                                : "Assign and review daily jobs across the company. Track completion, on-time delivery, and individual performance.")
                            : "Your assigned tasks. Mark each as in progress or done as you work through them."}
                    </p>
                </div>
                {isManager && (
                    <button
                        onClick={openCreate}
                        data-testid="task-new-btn"
                        className="inline-flex items-center gap-2 bg-klein text-white px-5 py-3 text-sm hover:bg-klein/90"
                    >
                        <Plus className="w-4 h-4" /> Assign task
                    </button>
                )}
            </div>

            {/* KPIs */}
            <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-px bg-ink/10 border border-ink/10">
                <KpiCell label="Total" value={counts.total} icon={ListTodo} testid="task-kpi-total" />
                <KpiCell label="Pending" value={counts.pending} icon={Clock} testid="task-kpi-pending" />
                <KpiCell label="In progress" value={counts.in_progress} icon={Clock} testid="task-kpi-in-progress" />
                <KpiCell label="Done" value={counts.done} icon={CheckCircle2} testid="task-kpi-done" />
                <KpiCell label="Overdue" value={counts.late} icon={AlertTriangle} accent={counts.late > 0} testid="task-kpi-overdue" />
            </div>

            {/* Filters */}
            <div className="mt-8 bg-white border border-ink/10 p-5 flex flex-wrap gap-3 items-end" data-testid="task-filters">
                <div className="flex items-center gap-2 text-ink/60 text-xs uppercase tracking-[0.15em]">
                    <Filter className="w-4 h-4" /> Filters
                </div>
                <Field label="Status">
                    <select
                        value={filter.status}
                        onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
                        className="border border-ink/15 px-3 py-2 text-sm bg-white"
                        data-testid="task-filter-status"
                    >
                        <option value="all">All</option>
                        <option value="pending">Pending</option>
                        <option value="in_progress">In progress</option>
                        <option value="done">Done</option>
                        <option value="cancelled">Cancelled</option>
                    </select>
                </Field>
                {isManager && (
                    <Field label="Employee">
                        <EmployeePicker employees={employees} value={filter.employee_id} onChange={(v) => setFilter((f) => ({ ...f, employee_id: v }))} allLabel="All employees" className="min-w-[220px]" testid="task-filter-employee" />
                    </Field>
                )}
                <Field label="From">
                    <input type="date" value={filter.from_date} onChange={(e) => setFilter((f) => ({ ...f, from_date: e.target.value }))} className="border border-ink/15 px-3 py-2 text-sm bg-white" data-testid="task-filter-from" />
                </Field>
                <Field label="To">
                    <input type="date" value={filter.to_date} onChange={(e) => setFilter((f) => ({ ...f, to_date: e.target.value }))} className="border border-ink/15 px-3 py-2 text-sm bg-white" data-testid="task-filter-to" />
                </Field>
                {(filter.status !== "all" || filter.employee_id || filter.from_date || filter.to_date) && (
                    <button
                        onClick={() => setFilter({ status: "all", employee_id: "", from_date: "", to_date: "" })}
                        className="text-xs text-ink/60 hover:text-klein underline"
                        data-testid="task-filter-clear"
                    >Clear</button>
                )}
            </div>

            {/* Table */}
            <div className="mt-6 bg-white border border-ink/10 overflow-x-auto" data-testid="task-table-wrap">
                <table className="w-full text-sm">
                    <thead className="bg-ink/5 text-ink/60 text-[10px] uppercase tracking-[0.15em]">
                        <tr>
                            <th className="text-left p-3">Task</th>
                            {isManager && <th className="text-left p-3">Assignee</th>}
                            <th className="text-left p-3">Due</th>
                            <th className="text-left p-3">Priority</th>
                            <th className="text-left p-3">Status</th>
                            <th className="text-left p-3">Score</th>
                            <th className="text-right p-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={isManager ? 7 : 6} className="p-6 text-ink/40 text-center">Loading…</td></tr>
                        ) : tasks.length === 0 ? (
                            <tr><td colSpan={isManager ? 7 : 6} className="p-6 text-ink/40 text-center" data-testid="task-empty-state">No tasks match the current filters.</td></tr>
                        ) : tasks.map((t) => {
                            const isLate = t.status !== "done" && t.status !== "cancelled" && t.due_date && t.due_date < todayISO();
                            return (
                                <tr key={t.id} className="border-t border-ink/5 hover:bg-bone" data-testid={`task-row-${t.id}`}>
                                    <td className="p-3 min-w-[220px]">
                                        <div className="font-medium text-ink">{t.title}</div>
                                        {t.description && <div className="text-xs text-ink/50 mt-0.5 line-clamp-2">{t.description}</div>}
                                    </td>
                                    {isManager && (
                                        <td className="p-3 text-ink/70 text-xs">
                                            {t.employee_name || "—"}
                                            {t.department && <div className="text-ink/40">{t.department}</div>}
                                        </td>
                                    )}
                                    <td className="p-3 whitespace-nowrap">
                                        <span className={isLate ? "text-red-600 font-medium" : "text-ink"}>{t.due_date || "—"}</span>
                                        {isLate && <span className="ml-2 text-[10px] uppercase tracking-[0.12em] text-red-600">Overdue</span>}
                                    </td>
                                    <td className="p-3">
                                        <span className={`inline-flex items-center px-2 py-0.5 border text-[10px] uppercase tracking-[0.12em] ${PRIORITY_PILL[t.priority] || PRIORITY_PILL.normal}`}>
                                            {t.priority || "normal"}
                                        </span>
                                    </td>
                                    <td className="p-3">
                                        <span className={`inline-flex items-center px-2 py-0.5 border text-[10px] uppercase tracking-[0.12em] ${STATUS_PILL[t.status] || STATUS_PILL.pending}`}>
                                            {(t.status || "pending").replace(/_/g, " ")}
                                        </span>
                                    </td>
                                    <td className="p-3 text-ink/70 tabular-nums">{t.score == null ? "—" : t.score}</td>
                                    <td className="p-3 text-right whitespace-nowrap">
                                        <div className="inline-flex items-center gap-2">
                                            {t.status !== "done" && (
                                                <button
                                                    onClick={() => markStatus(t, t.status === "pending" ? "in_progress" : "done")}
                                                    className="text-xs px-2 py-1 border border-klein text-klein hover:bg-klein hover:text-white"
                                                    data-testid={`task-quick-${t.id}`}
                                                >
                                                    {t.status === "pending" ? "Start" : "Done"}
                                                </button>
                                            )}
                                            <button
                                                onClick={() => openEdit(t)}
                                                className="text-ink/60 hover:text-klein"
                                                data-testid={`task-edit-${t.id}`}
                                                aria-label="Edit"
                                            ><Pencil className="w-4 h-4" /></button>
                                            {isManager && (
                                                <button
                                                    onClick={() => remove(t)}
                                                    className="text-ink/60 hover:text-red-600"
                                                    data-testid={`task-delete-${t.id}`}
                                                    aria-label="Delete"
                                                ><Trash2 className="w-4 h-4" /></button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {dialogOpen && (
                <TaskDialog
                    form={form}
                    setForm={setForm}
                    employees={employees}
                    isManager={isManager}
                    isEdit={!!form.id}
                    saving={saving}
                    onClose={closeDialog}
                    onSave={save}
                    myEmail={user?.email}
                />
            )}
        </div>
    );
}

function KpiCell({ label, value, icon: Icon, accent, testid }) {
    return (
        <div className={`p-5 ${accent ? "bg-red-600 text-white" : "bg-white"}`} data-testid={testid}>
            <div className="flex items-start justify-between">
                <div className={`overline ${accent ? "!text-white/70" : ""}`}>{label}</div>
                <Icon className={`w-4 h-4 ${accent ? "text-white/70" : "text-ink/40"}`} strokeWidth={1.5} />
            </div>
            <div className="mt-4 font-display font-black text-3xl tracking-tighter tabular-nums">{value}</div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-[0.15em] text-ink/50">{label}</span>
            {children}
        </label>
    );
}

function TaskDialog({ form, setForm, employees, isManager, isEdit, saving, onClose, onSave, myEmail }) {
    const onField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="task-dialog">
            <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-5 border-b border-ink/10 flex items-center justify-between">
                    <div>
                        <div className="overline">{isEdit ? "Edit task" : "Assign task"}</div>
                        <h3 className="mt-1 font-display font-bold text-2xl tracking-tight text-ink">
                            {isEdit ? form.title : "New task"}
                        </h3>
                    </div>
                </div>
                <div className="p-6 space-y-4">
                    {!isEdit && isManager && (
                        <Field label="Employee *">
                            <EmployeePicker employees={employees} value={form.employee_id} onChange={(v) => setForm((f) => ({ ...f, employee_id: v }))} placeholder="— Select —" testid="task-form-employee" />
                        </Field>
                    )}
                    <Field label="Title *">
                        <input value={form.title} onChange={onField("title")} disabled={!isManager && isEdit} className="border border-ink/15 px-3 py-2 text-sm bg-white" data-testid="task-form-title" />
                    </Field>
                    <Field label="Description">
                        <textarea value={form.description} onChange={onField("description")} disabled={!isManager && isEdit} rows={3} className="border border-ink/15 px-3 py-2 text-sm bg-white" data-testid="task-form-description" />
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Due date *">
                            <input type="date" value={form.due_date} onChange={onField("due_date")} disabled={!isManager && isEdit} className="border border-ink/15 px-3 py-2 text-sm bg-white" data-testid="task-form-due" />
                        </Field>
                        <Field label="Priority">
                            <select value={form.priority} onChange={onField("priority")} disabled={!isManager && isEdit} className="border border-ink/15 px-3 py-2 text-sm bg-white" data-testid="task-form-priority">
                                <option value="low">Low</option>
                                <option value="normal">Normal</option>
                                <option value="high">High</option>
                            </select>
                        </Field>
                    </div>
                    {isEdit && (
                        <>
                            <div className="grid grid-cols-2 gap-4">
                                <Field label="Status">
                                    <select value={form.status} onChange={onField("status")} className="border border-ink/15 px-3 py-2 text-sm bg-white" data-testid="task-form-status">
                                        <option value="pending">Pending</option>
                                        <option value="in_progress">In progress</option>
                                        <option value="done">Done</option>
                                        {isManager && <option value="cancelled">Cancelled</option>}
                                    </select>
                                </Field>
                                {isManager && (
                                    <Field label="Score (0–100)">
                                        <input type="number" min="0" max="100" step="1" value={form.score} onChange={onField("score")} className="border border-ink/15 px-3 py-2 text-sm bg-white" data-testid="task-form-score" />
                                    </Field>
                                )}
                            </div>
                            <Field label="Completion note">
                                <textarea value={form.completion_note} onChange={onField("completion_note")} rows={2} className="border border-ink/15 px-3 py-2 text-sm bg-white" data-testid="task-form-note" />
                            </Field>
                            <TaskAttachments
                                taskId={form.id}
                                initial={form.attachments || []}
                                canDeleteAll={isManager}
                                myEmail={myEmail}
                                onChange={(attachments) => setForm((f) => ({ ...f, attachments }))}
                            />
                        </>
                    )}
                    {!isEdit && (
                        <p className="text-xs text-ink/40">You can attach documents or photos after the task is created.</p>
                    )}
                </div>
                <div className="px-6 py-4 border-t border-ink/10 flex justify-end gap-2">
                    <button onClick={onClose} disabled={saving} className="px-4 py-2 text-sm border border-ink/15 hover:bg-ink hover:text-white" data-testid="task-form-cancel">Cancel</button>
                    <button onClick={onSave} disabled={saving} className="px-4 py-2 text-sm bg-klein text-white hover:bg-klein/90" data-testid="task-form-save">
                        {saving ? "Saving…" : (isEdit ? "Save changes" : "Assign task")}
                    </button>
                </div>
            </div>
        </div>
    );
}
